import React, { useEffect, useState } from "react";

import RecentHistory from "./CommonJobBoardTabs/RecentHistory";
import SavedHistory from "./CommonJobBoardTabs/SavedHistory";
import SearchResults from "./SearchResults";
import {
  Backdrop,
  Button,
  FormFieldSet,
  LogoLoader,
  NewTab,
  showToast,
  useFormHandler,
} from "../../../components";
import {
  degreesOptions,
  workAuthorizationOptions,
} from "../../../assets/data/enumData";
import HorizontalScale from "../../../components/Common/HorizontalScale";
import { useDispatch, useSelector } from "react-redux";
import { getAllApplicantsDataStart } from "../../../redux/slices/internalDbES/internalDbESlice";
import {
  getCountries,
  getLocations,
} from "../../../redux/slices/internalDbES/internalDbESApi";
import { normalizeQuotes } from "../../../utils/common";

const buildBooleanQuery = ({ include = "", exclude = "" }) => {
  const extractKeywords = (text) => {
    // 1. Remove parentheses
    text = text.replace(/[()]/g, " ");

    // 2. Extract quoted phrases OR single words
    const matches = [...text.matchAll(/"([^"]+)"|(\S+)/g)];

    const keywords = matches
      .map((m) => (m[1] || m[2]).trim())
      .filter(Boolean)
      .filter((k) => !["and", "or", "not"].includes(k.toLowerCase()));

    return keywords;
  };

  const includeKeywords = extractKeywords(include);
  const excludeKeywords = extractKeywords(exclude);

  const includePart = includeKeywords.map((k) => `"${k}"`).join(" OR ");
  const excludePart = excludeKeywords.map((k) => `NOT "${k}"`).join(" AND ");

  if (includePart && excludePart) return `${includePart} AND ${excludePart}`;
  if (includePart) return includePart;
  if (excludePart) return excludePart;
  return "";
};

const parseLocation = (value = "") => {
  const parts = value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

  let city = "";
  let state = "";
  let zipCode = "";

  if (parts.length === 3) {
    // city,state,zip
    [city, state, zipCode] = parts;
  } else if (parts.length === 1) {
    // single value → ONLY state
    state = parts[0];
  }

  return { city, state, zipCode };
};

const InternalDb = () => {
  const {
    totalApplicantsData: totalApplicants,
    totalApplicantsCount,
    totalRecords,
  } = useSelector((state) => state.internalDbES);
  const { user } = useSelector((state) => state.auth);

  const { loading } = useSelector((state) => state.internalDbES);
  const [validationRules, setValidationRules] = useState({});
  const [advancedSearch, setAdvancedSearch] = useState(false);
  const [searchApplicant, setSearchApplicant] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [locationValue, setLocationValue] = useState(1);
  const [string, setString] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [exclude, setExclude] = useState("");
  const [locationTypingValue, setLocationTypingValue] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState({});
  const [isSelection, setIsSelection] = useState(false);

  const handleSelect = ({ title, filterTerms = {} }) => {
    const normalizedFilterTerms = filterTerms;
    if (normalizedFilterTerms?.location?.zipCode) {
      setLocationValue(2);
      const zipLocation = `${normalizedFilterTerms?.location?.city},${normalizedFilterTerms?.location?.state},${normalizedFilterTerms?.location?.zipCode},`;
      setLocationTypingValue(zipLocation);
    } else {
      setLocationValue(1);
    }

    const initialMappedData = {
      schedule: "",
      date: "",
      limit: "",
      interval: "",
      harvestFrom: "",

      //skills: [],
      skillWiseExperience: "",

      exp: {
        year: normalizedFilterTerms?.exp?.year ?? "",
        mon: normalizedFilterTerms?.exp?.mon ?? "",
      },

      location: {
        country: normalizedFilterTerms?.location?.country ?? "United States",
        state: normalizedFilterTerms?.location?.state ?? "",
        city: normalizedFilterTerms?.location?.city ?? "",
        zipCode: normalizedFilterTerms?.location?.zipCode ?? "",
      },

      // stateOrCity:
      //   normalizedFilterTerms?.location?.city ??
      //   normalizedFilterTerms?.location?.state ??
      //   "",

      relocation: {
        willingness:
          normalizedFilterTerms?.relocation?.willingness,
        // location: {
        //   country: {
        //     code: "",
        //     name: normalizedFilterTerms?.location?.country ?? "",
        //   },
        //   state: {
        //     code: normalizedFilterTerms?.location?.state ?? "",
        //   },
        //   city: normalizedFilterTerms?.location?.city ?? "",
        //   zipCode: "",
        // },
      },

      languagesKnown: normalizedFilterTerms?.languagesKnown ?? "",

      gender: normalizedFilterTerms?.gender ?? "",

      educationDetails: {
        degree: normalizedFilterTerms?.educationDetails?.degree ?? "",
        institute: "",
        major: [],
        minor: [],
        gpa: null,
        endDate: "",
        location: {
          country: {
            code: "",
          },
          state: {
            name: "",
            code: "",
          },
          city: "",
          zipCode: "",
        },
      },

      salaryInfo: {
        payRate: {
          amount: {
            min: normalizedFilterTerms?.salaryInfo?.payRate?.amount?.min ?? "",
            max: normalizedFilterTerms?.salaryInfo?.payRate?.amount?.max ?? "",
          },
        },
        expect: {
          amount: {
            min: normalizedFilterTerms?.salaryInfo?.expect?.amount?.min ?? "",
            max: normalizedFilterTerms?.salaryInfo?.expect?.amount?.max ?? "",
          },
        },
      },

      subQualification: "",
      employmentType: "",

      workAuth: normalizedFilterTerms?.workAuth ?? [],

      candidateStatus: "",
      poolStatus: "",
      workedInCountry: "",
      candidateType: "",
      lastUpdatedOn: "",
      workLocationStatus: "",

      currentEmp:
        (normalizedFilterTerms?.currentEmp &&
          Boolean(normalizedFilterTerms?.currentEmp)) ??
        "",

      currentSalary: "",
      currentSalaryMin: "",
      currentSalaryMax: "",

      expectedSalary: "",
      expectedSalaryMin:
        normalizedFilterTerms?.salaryInfo?.expect?.amount?.min ?? "",
      expectedSalaryMax:
        normalizedFilterTerms?.salaryInfo?.expect?.amount?.max ?? "",

      totalYearsOfExperience: normalizedFilterTerms?.exp?.year ?? "",
      monthsOfExperience: normalizedFilterTerms?.exp?.mon ?? "",

      source: normalizedFilterTerms?.source ?? "",

      domain: "",
      keywords: null,
      jobTitle: normalizedFilterTerms?.jobTitle ?? "",

      excludePreviousEmployer:
        normalizedFilterTerms?.excludePreviousEmployer ?? "",

      // locationValue:
      //   normalizedFilterTerms?.location.city &&
      //   normalizedFilterTerms?.location.state
      //     ? `${normalizedFilterTerms?.location?.city}, ${normalizedFilterTerms?.location?.state}`
      //     : "",

      // locationType: {
      //   city: normalizedFilterTerms?.location?.city ?? "",
      //   zip: "",
      //   country: normalizedFilterTerms?.location?.country ?? "",
      //   state: normalizedFilterTerms?.location?.state ?? "",
      // },

      radius: "",
      skills: normalizedFilterTerms?.skills ?? [],
    };

    setFormData({
      ...initialMappedData,
      keywords: `${title}`,
    });
  };
  // console.log(formData)

  const initialFormData = {
    schedule: "",
    date: "",
    limit: "",
    interval: "",
    harvestFrom: "",
    skills: [],
    skillWiseExperience: "",
    exp: {
      year: "",
      mon: "",
    },
    location: {
      country: "United States",
      state: "",
      city: "",
      zipCode: "",
    },
    stateOrCity: "",
    relocation: {
      willingness: null,
      location: {
        country: {
          code: "",
          name: "",
        },
        state: {
          code: "",
        },
        city: "",
        zipCode: "",
      },
    },
    languagesKnown: "",
    gender: "",
    educationDetails: {
      degree: "",
      institute: "",
      major: [],
      minor: [],
      gpa: null,
      endDate: "",
      location: {
        country: {
          code: "",
        },
        state: {
          name: "",
          code: "",
        },
        city: "",
        zipCode: "",
      },
    },
    salaryInfo: {
      payRate: {
        amount: {
          min: "",
          max: "",
        },
      },
      expect: {
        amount: {
          min: "",
          max: "",
        },
      },
    },
    subQualification: "",
    employmentType: "",
    workAuth: [],
    candidateStatus: "",
    poolStatus: "",
    workedInCountry: "",
    candidateType: "",
    lastUpdatedOn: "",
    workLocationStatus: "",
    currentEmp: "",
    currentSalary: "",
    currentSalaryMin: "",
    currentSalaryMax: "",
    expectedSalary: "",
    expectedSalaryMin: "",
    expectedSalaryMax: "",
    totalYearsOfExperience: "",
    monthsOfExperience: "",
    source: "",
    domain: "",
    keywords: null,
    jobTitle: "",
    excludePreviousEmployer: "",
    locationValue: "",
    locationType: {
      city: "",
      zip: "",
      country: "",
      state: "",
    },
    radius: "",
  };
  useEffect(() => {
    const baseRules = {
      searchKeyword: {
        type: "text",
        required: true,
      },
      country: {
        type: "select",
        required: true,
      },
      workAuth: {
        type: "select",
        required: true,
      },
    };
    setValidationRules(baseRules);
  }, []);

  const { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler(initialFormData, validationRules);

  
  useEffect(() => {}, [formData?.skills]);

  const [updatedAction, setUpdatedAction] = useState();
  const dispatch = useDispatch();

  const areAllFieldsMissing = (input) => {
    if (input === undefined || input === null || input === "") return true;
    if (Array.isArray(input)) {
      return input.length === 0 || input.every(areAllFieldsMissing);
    }
    if (typeof input === "object") {
      const values = Object.values(input);
      return values.length === 0 || values.every(areAllFieldsMissing);
    }
    return false;
  };

  const onSuccess = () => {
    setFormData(initialFormData);
    setLocationValue("");
    setSelectedTitle("");
    if (searchApplicant === true) return;
    setSearchApplicant((prev) => !prev);
  };

  // const cleanData = (formData) => {
  //   const flatten = (obj, prefix = "", result = {}) => {
  //     if (Array.isArray(obj)) {
  //       // ✅ Instead of breaking into indexed keys, preserve array
  //       result[prefix] = obj;
  //     } else if (typeof obj === "object" && obj !== null) {
  //       for (const key in obj) {
  //         if (Object.hasOwn(obj, key)) {
  //           const value = obj[key];
  //           const newKey = prefix ? `${prefix}.${key}` : key;
  //           flatten(value, newKey, result);
  //         }
  //       }
  //     } else {
  //       result[prefix] = obj;
  //     }
  //     return result;
  //   };

  //   const flattened = flatten(formData);

  //   const cleanFormData = {};
  //   for (const key in flattened) {
  //     const value = flattened[key];

  //     // keep arrays intact; skip only if it's empty
  //     if (
  //       value !== "" &&
  //       value !== null &&
  //       value !== undefined &&
  //       !(Array.isArray(value) && value.length === 0)
  //     ) {
  //       cleanFormData[key] = value;
  //     }
  //   }

  //   return cleanFormData;
  // };

  const cleanData = (data) => {
    const isEmptyObject = (obj) =>
      typeof obj === "object" &&
      obj !== null &&
      !Array.isArray(obj) &&
      Object.keys(obj).length === 0;

    // remove empty primitives
    if (data === "" || data === null || data === undefined) {
      return undefined;
    }

    // unwrap { value, label? }
    if (
      typeof data === "object" &&
      data !== null &&
      !Array.isArray(data) &&
      Object.prototype.hasOwnProperty.call(data, "value") &&
      (Object.keys(data).length === 1 ||
        (Object.keys(data).length === 2 &&
          Object.prototype.hasOwnProperty.call(data, "label")))
    ) {
      return data.value;
    }

    // handle arrays
    if (Array.isArray(data)) {
      const cleanedArray = data
        .map(cleanData)
        .filter((item) => item !== undefined);

      return cleanedArray.length ? cleanedArray : undefined;
    }

    // handle objects
    if (typeof data === "object") {
      const cleanedObject = {};

      Object.keys(data).forEach((key) => {
        const cleanedValue = cleanData(data[key]);

        if (cleanedValue !== undefined && !isEmptyObject(cleanedValue)) {
          cleanedObject[key] = cleanedValue;
        }
      });

      return Object.keys(cleanedObject).length ? cleanedObject : undefined;
    }

    // primitive value
    return data;
  };

  const handleSearchApplicants = async (
    state = null,
    newFormData,
    historypayload,
  ) => {
    if (!newFormData?.keywords && !formData?.keywords) {
      showToast("Keywords Missing", "error");
      return;
    }

    const keyword = formData?.keywords || newFormData?.keywords;
    const openParen = (keyword.match(/\(/g) || []).length;
    const closeParen = (keyword.match(/\)/g) || []).length;

    if (openParen !== closeParen) {
      showToast("Invalid keyword syntax: missing ')'.", "error", 2000);
      return;
    }

    // check unbalanced double quotes
    const quotes = (keyword.match(/"/g) || []).length;

    if (quotes % 2 !== 0) {
      showToast('Invalid keyword syntax: missing closing "', "error", 2000);
      return;
    }

    // Usage
    const cleanedHistory = historypayload;

    const data = newFormData ? newFormData : formData;
    const cleanedFormData = cleanData(data);

    try {
      const currentAction = {
        payload: {
          query: { ...cleanedFormData, ...cleanedHistory },
          lim: 25,
          page: 1,
          addedBy: user?.data?.uiid,
          source: 1,
        },
      };

      setUpdatedAction(currentAction);
      dispatch(
        getAllApplicantsDataStart({
          action: searchApplicant ? state : currentAction,
          onSuccess,
        }),
      );
    } catch (error) {
      console.log(error);
    }
  };
  const onSearch = async ({ title, filterTerms }) => {
    if (!title) return;

    setSelectedTitle(title);
    const newForm = { ...formData, keywords: `${title}` };

    setFormData(newForm);
    //console.log(newForm);
    await handleSearchApplicants(null, newForm, filterTerms);
    setSelectedTitle("");
  };
  const internalDbMenus = [
    {
      title: "Recent History",
      page: (
        <RecentHistory
          board="internalDb"
          onSelect={handleSelect}
          onClick={onSearch}
        />
      ),
      type: "main",
    },
    {
      title: "Saved History",
      page: (
        <SavedHistory
          board="internalDb"
          onSelect={handleSelect}
          onClick={onSearch}
        />
      ),
      type: "main",
    },
  ];

  const formatDragAndDrop = (keywordArray) => {
    if (!Array.isArray(keywordArray) || keywordArray.length === 0) {
      //setKeywordString("");
      return;
    }

    const formatted = (keywordArray || [])
      .map((k, i) => {
        const connector = k?.connector?.trim() || "";
        const keyword = k?.keyword?.trim() || selectedTitle || "";
        return i > 0 && connector
          ? `${connector} "${keyword}"`
          : `"${keyword}"`;
      })
      .join(" ");

    handleChange("keywords", formatted);
  };

  useEffect(() => {
    // if (!locationTypingValue) return;
    if (!locationTypingValue || isSelection) {
      setIsSelection(false);
      return;
    }
    // const [city, state, zip] = locationTypingValue.split(",");
    // if (city && state && zip) return;
    const [city, state, zip] = locationTypingValue.split(",");
    if (city && state) {
      setLocations([]);
      return;
    }

    const delay = setTimeout(async () => {
      setLocations([]);
      const response = await getLocations({
        locationValue: locationTypingValue,
        country: selectedCountry?._id,
        limit: 500,
      });
      if (response?.success) {
        // const locationData = response?.data?.data
        //   .map((item) => {
        //     const name = item.name ?? "";
        //     const state = item.stateName ?? "";
        //     const zip = item.zipCode ?? "";

        //     return `${name},${state},${zip}`.trim();
        //   });
        const uniqueStates = [
          ...new Set(
            (response?.data?.data || []).map((item) => item.stateName),
          ),
        ];
        const locationData = response?.data?.data.flatMap((item) => {
          const name = item.name ?? "";
          const state = item.stateName ?? "";

          // If zipCode is array, return one entry per zip
          if (Array.isArray(item.zipCode)) {
            return item.zipCode.map((zc) => `${name},${state},${zc}`);
          }

          // If zipCode is a single string
          return [`${name},${state},${item.zipCode ?? ""}`];
        });
        // setLocations([...uniqueStates, ...locationData]);
        const search = locationTypingValue.toLowerCase();
        const statesStartsWith = uniqueStates.filter((item) =>
          item.toLowerCase().startsWith(search),
        );
        const statesContains = uniqueStates.filter(
          (item) =>
            item.toLowerCase().includes(search) &&
            !item.toLowerCase().startsWith(search),
        );
        const locationsStartsWith = locationData.filter((item) =>
          item.toLowerCase().startsWith(search),
        );
        const locationsContains = locationData.filter(
          (item) =>
            item.toLowerCase().includes(search) &&
            !item.toLowerCase().startsWith(search),
        );
        statesStartsWith.sort((a, b) => a.localeCompare(b));
        statesContains.sort((a, b) => a.localeCompare(b));
        locationsStartsWith.sort((a, b) => a.localeCompare(b));
        locationsContains.sort((a, b) => a.localeCompare(b));
        setLocations([
          ...statesStartsWith,
          ...locationsStartsWith,
          ...statesContains,
          ...locationsContains,
        ]);
      }
    }, 0);

    return () => clearTimeout(delay);
  }, [locationTypingValue, formData?.country]);

  useEffect(() => {
    if (!formData?.location?.country) return;

    const delay = setTimeout(async () => {
      const response = await getCountries({
        name: formData?.location?.country,
      });
      const countries = response.data.data;
      const desiredCountry = countries[0];

      setSelectedCountry(desiredCountry);
      setLocationTypingValue("");
    }, 0);

    return () => clearTimeout(delay);
  }, [formData?.location?.country]);

  // if (loading) {
  //   return (
  //     <div className="flex justify-center items-center h-screen">
  //       <LogoLoader />
  //     </div>
  //   );
  // }
  //console.log(selectedTitle)
  return !searchApplicant ? (
    <div className="flex flex-1 flex-col p-2 overflow-auto">
      <p className="px-4 py-2 font-[600]">Applicant Search</p>
      <div className="p-2 flex flex-1 md:flex-col lg:flex-row  shadow">
        <div>
          <div className=" grid grid-cols-1 m-5 gap-3">
            <div className="flex flex-col gap-2 inner-section rounded p-2">
              <FormFieldSet
                title="Keywords"
                placeholder="Enter Keywords"
                value={formData.keywords}
                onChange={(value) =>
                  handleChange("keywords", normalizeQuotes(value))
                }
                error={errors.keywords}
              />
              <FormFieldSet
                title="Job Title"
                placeholder="Enter Keywords"
                value={formData.jobTitle}
                onChange={(value) =>
                  handleChange("jobTitle", normalizeQuotes(value))
                }
                error={errors.jobTitle}
              />
            </div>
            <div className="p-2 inner-section rounded">
              <h6>Location</h6>
              <div className="flex flex-col gap-2">
                <FormFieldSet
                  name={"location"}
                  type="radio"
                  noTitle
                  options={[
                    { label: "Search Within Selected Locations", value: 1 },
                    { label: "Search Within Selected ZipCode", value: 2 },
                    // { label: "Country/State", value: 3 },
                  ]}
                  value={locationValue}
                  onChange={(event) => setLocationValue(event.target.value)}
                />
                <div className="grid grid-cols-3 gap-2">
                  {locationValue == 2 && (
                    <>
                      <FormFieldSet
                        noTitle
                        type="countriesUsAndCanada"
                        placeholder="Select Country"
                        value={formData?.location?.country}
                        onChange={(value) => {
                          handleChange("location.country", value.value);
                        }}
                        shadow={true}
                      />
                      <FormFieldSet
                        noTitle
                        placeholder="Enter ZipCode"
                        value={locationTypingValue}
                        onChange={(value) => {
                          setLocationTypingValue(value);
                          setIsSelection(locations.includes(value));

                          const { city, state, zipCode } = parseLocation(value);
                          if (locations.includes(value)) {
                            setLocations([]);
                          }
                          handleChange("location.city", city || "");
                          handleChange("location.state", state || "");
                          handleChange("location.zipCode", zipCode || "");
                          // setLocations([]);
                        }}
                        suggestionsList={locations}
                      />
                      <FormFieldSet
                        noTitle
                        placeholder="Enter Radius"
                        value={formData?.radius}
                        onChange={(value) =>
                          handleChange("radius", Number(value))
                        }
                      />
                    </>
                  )}
                  {locationValue == 1 && (
                    <>
                      <FormFieldSet
                        noTitle
                        type="countriesUsAndCanada"
                        placeholder="Select Country"
                        value={formData?.location?.country}
                        onChange={(value) => {
                          handleChange("location.country", value.value);
                        }}
                        shadow={true}
                      />
                      <div className="w-full">
                        <FormFieldSet
                          noTitle
                          type="states"
                          multiple
                          showSearch
                          placeholder="Select State"
                          value={formData?.location?.state}
                          onChange={(value) => {
                            handleChange("location.state", value);
                          }}
                          error={errors?.location?.state}
                          parentValue={{
                            country: formData?.location?.country,
                            // country: "Canada",
                          }}
                          shadow={true}
                        />
                      </div>
                      <div className="w-full">
                        <FormFieldSet
                          noTitle
                          type="cities"
                          multiple
                          showSearch
                          placeholder="Select City"
                          value={formData?.location?.city}
                          onChange={(value) => {
                            handleChange("location.city", value);
                          }}
                          error={errors?.location?.city}
                          parentValue={{
                            country: formData?.location?.country,
                            state: formData?.location?.state,
                            // country: "Canada",
                          }}
                          shadow={true}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
              {/* <div className="my-2">
                {locationValue == 2 && <HorizontalScale />}
              </div> */}
            </div>
            <div className="px-2 py-4 inner-section rounded grid grid-cols-2 gap-4">
              <h6>Candidate Details</h6>
              <div className="col-span-8">
                <FormFieldSet
                  name={"willingness"}
                  type="radio"
                  noTitle
                  options={[
                    {
                      label: "Willing To Relocate",
                      value: true,
                    },
                    {
                      label: "Only Recommended Candidates",
                      value: false,
                    },
                  ]}
                  value={formData.relocation?.willingness}
                  onChange={(value) => {
                    console.log(value.target.value);
                    handleChange(
                      "relocation.willingness",
                      Boolean(value.target.value),
                    );
                  }}
                  error={errors.relocation?.willingness}
                />
              </div>
              <div className="grid md:grid-cols-2 xl:grid-cols-4 col-span-8 gap-4">
                {/* <FormFieldSet
                  title="Languages Known"
                  placeholder="Languages Known"
                  value={formData.languagesKnown}
                  onChange={(value) => handleChange("languagesKnown", value)}
                  error={errors.languagesKnown}
                /> */}
                <FormFieldSet
                  title="Gender"
                  placeholder="Select"
                  type="select"
                  options={[
                    { label: "Male", value: 1 },
                    { label: "Female", value: 2 },
                  ]}
                  value={formData.gender}
                  onChange={(value) => handleChange("gender", value)}
                  error={errors.gender}
                />
                <FormFieldSet
                  title="Qualification"
                  placeholder="Select"
                  type="select"
                  value={formData.educationDetails?.degree}
                  onChange={(value) =>
                    handleChange("educationDetails.degree", value)
                  }
                  error={errors.educationDetails?.degree}
                  options={degreesOptions}
                  showSearch={true}
                />
                {/* <FormFieldSet
            title="Sub Qualification"
            placeholder="Select"
            type="select"
            value={formData.subQualification}
            onChange={(value) => handleChange("subQualification", value)}
            error={errors.subQualification}
          /> */}
                {/* <FormFieldSet
            title="Employment Type"
            placeholder="Select"
            type="select"
            value={formData.employmentType}
            onChange={(value) => handleChange("employmentType", value)}
            error={errors.employmentType}
          /> */}
                <FormFieldSet
                  title="Work Authorization"
                  placeholder="Select"
                  type="select"
                  value={formData.workAuth}
                  onChange={(value) => handleChange("workAuth", value)}
                  error={errors.workAuth}
                  options={workAuthorizationOptions}
                  showSearch={true}
                  multiple={true}
                />
                {/* <FormFieldSet
            title="Candidate Status"
            placeholder="Select"
            type="select"
            value={formData.candidateStatus}
            onChange={(value) => handleChange("candidateStatus", value)}
            error={errors.candidateStatus}
          />
          <FormFieldSet
            title="Pool Status"
            placeholder="Select"
            type="select"
            value={formData.poolStatus}
            onChange={(value) => handleChange("poolStatus", value)}
            error={errors.poolStatus}
          /> */}

                <FormFieldSet
                  title="Worked in (Country)"
                  placeholder="Select"
                  type="select"
                  value={formData.location?.country}
                  onChange={(value) => handleChange("location.country", value)}
                  error={errors.location?.country}
                  options={[
                    { label: "United States", value: "US" },
                    { label: "Canada", value: "CA" },
                  ]}
                />
              </div>
            </div>
            <div className="px-2 py-4 inner-section rounded">
              <h6 className="my-1">Candidate Preference</h6>
              <div className="grid grid-cols-3 gap-4 my-2">
                {/* <FormFieldSet
              title="Candidate Type"
              type="select"
              placeholder="Candidate Type"
              value={formData.candidateType}
              onChange={(value) => handleChange("candidateType", value)}
              error={errors.candidateType}
            /> */}
                <FormFieldSet
                  title="Last Updated On"
                  type="datepicker"
                  placeholder="Last Updated On"
                  value={formData.lastUpdatedOn}
                  onChange={(value) => handleChange("lastUpdatedOn", value)}
                  error={errors.lastUpdatedOn}
                />
                {/* <FormFieldSet
                  title="Work Location Status"
                  type="multiSelect"
                  placeholder="Select"
                  value={formData.workLocationStatus}
                  onChange={(value) =>
                    handleChange("workLocationStatus", value)
                  }
                  error={errors.workLocationStatus}
                  options={[
                    { label: "Remote", value: "remote" },
                    { label: "On-site", value: "onsite" },
                    { label: "Hybrid", value: "hybrid" },
                    { label: "Any", value: "any" },
                  ]}
                /> */}
                {/* <FormFieldSet
                  title="Previous Employer"
                  type="select"
                  placeholder="Select"
                  value={formData.currentEmp}
                  onChange={(value) => handleChange("currentEmp", value)}
                  error={errors.currentEmp}
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                /> */}
                <FormFieldSet
                  title="Total years of Experience"
                  placeholder="Year"
                  value={formData.exp?.year}
                  onChange={(value) => handleChange("exp.year", value)}
                  error={errors.exp?.year}
                />
                <FormFieldSet
                  title=""
                  placeholder="Months"
                  value={formData.exp?.mon}
                  onChange={(value) => handleChange("exp.mon", value)}
                  error={errors.exp?.mon}
                />

                <FormFieldSet
                  type="select"
                  title="Source"
                  placeholder="Select"
                  value={formData.source}
                  onChange={(value) => handleChange("source", value)}
                  error={errors.source}
                  options={[
                    { label: "Internal DB", value: 1 },
                    { label: "Career Builder", value: 2 },
                    { label: "Monster", value: 3 },
                  ]}
                />
              </div>
              <div className="grid grid-cols-2 gap-4 my-2">
                <div className="grid grid-cols-2 gap-4">
                  <FormFieldSet
                    title="Current Salary"
                    placeholder="Min"
                    value={formData.salaryInfo?.payRate?.amount?.min}
                    onChange={(value) =>
                      handleChange("salaryInfo.payRate.amount.min", value)
                    }
                    error={errors.salaryInfo?.payRate?.amount?.min}
                  />
                  <FormFieldSet
                    title=""
                    placeholder="Max"
                    value={formData.salaryInfo?.payRate?.amount?.max}
                    onChange={(value) =>
                      handleChange("salaryInfo.payRate.amount.max", value)
                    }
                    error={errors.salaryInfo?.payRate?.amount?.min}
                  />
                  {/* <FormFieldSet
                    title=""
                    type="select"
                    placeholder="Select"
                    value={formData.currentSalary}
                    onChange={(value) => handleChange("currentSalary", value)}
                    error={errors.currentSalary}
                  /> */}
                </div>

                {/* Expected Salary */}
                <div className="grid grid-cols-2 gap-4">
                  <FormFieldSet
                    title="Expected Salary"
                    placeholder="Min"
                    value={formData.salaryInfo?.expect?.amount?.min}
                    onChange={(value) =>
                      handleChange("salaryInfo.expect.amount.min", value)
                    }
                    error={errors.salaryInfo?.expect?.amount?.min}
                  />
                  <FormFieldSet
                    title=""
                    placeholder="Max"
                    value={formData.salaryInfo?.expect?.amount?.max}
                    onChange={(value) =>
                      handleChange("salaryInfo.expect.amount.max", value)
                    }
                    error={errors.salaryInfo?.expect?.amount?.max}
                  />
                  {/* <FormFieldSet
                    title=""
                    type="select"
                    placeholder="Select"
                    value={formData.expectedSalary}
                    onChange={(value) => handleChange("expectedSalary", value)}
                    error={errors.expectedSalary}
                  /> */}
                </div>
              </div>

              {/* <div className="grid grid-cols-3 gap-4 my-2">
                
              </div> */}

              {/* <FormFieldSet
            type="select"
            title="Domain"
            placeholder="Select"
            value={formData.domain}
            onChange={(value) => handleChange("domain", value)}
            error={errors.domain}
          /> */}
            </div>
            <div className="flex flex-col inner-section px-2 py-4 rounded">
              <h6>Exclude</h6>
              <div className=" grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 md:grid-cols-1 gap-4 my-2">
                <FormFieldSet
                  title="Keywords"
                  placeholder="Keywords"
                  value={exclude}
                  onChange={(value) => {
                    setExclude(value);
                  }}
                  error={errors.keywords}
                />
                <FormFieldSet
                  title="Skills"
                  placeholder="Skills"
                  value={formData.skills}
                  onChange={(value) => handleChange("skills", value)}
                  error={errors.skills}
                />
                <FormFieldSet
                  title="Job Title"
                  placeholder="Job Title"
                  value={formData.jobTitle}
                  onChange={(value) => {
                    handleChange("jobTitle", value);
                    // keep the selectedTitle in-sync for other pieces that might use it
                    // setSelectedTitle(value);
                  }}
                  error={errors.jobTitle}
                />
                <FormFieldSet
                  title="Previous Employer"
                  placeholder="Previous Employer"
                  value={formData.excludePreviousEmployer}
                  onChange={(value) =>
                    handleChange("excludePreviousEmployer", value)
                  }
                  error={errors.excludePreviousEmployer}
                />
              </div>
            </div>
            <div className="flex justify-end items-center gap-4">
              {/* <Button variant="outlined" title={"Cancel"} /> */}
              <Button title={"Submit"} onClick={handleSearchApplicants} />
            </div>
          </div>
        </div>
        <div className="flex flex-1 flex-col mx-2 min-w-[30vw]">
          <p className="font-[600] mx-2">Strings History</p>
          <div className="flex flex-1 shadow m-2 p-3">
            <NewTab
              tabs={internalDbMenus}
              color="gray"
              variant="text"
              activeVariant="text"
            />
          </div>
        </div>
      </div>
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>
    </div>
  ) : (
    <div className="flex flex-1 flex-col overflow-hidden mt-2">
      <SearchResults
        source={1}
        query={updatedAction}
        setSearchApplicant={setSearchApplicant}
      />
    </div>
  );
};

export default InternalDb;
